
import { Component, Vue } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { getVersionDescriptions, getDescriptions, updateDescription } from '@/api/services/search-texts';
import { SearchTexts, UI, Project } from '@/types';
import { errorHandler } from '@/utils';
import ButtonComponent from '@/components/ButtonComponent.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import InputComponent from '@/components/InputComponent.vue';
import TextareaComponent from '@/components/TextareaComponent.vue';
import SpinnerComponent from '@/components/SpinnerComponent.vue';
import SearchComponent from '@/components/SearchComponent.vue';

@Component({
  components: {
    ButtonComponent,
    ModalComponent,
    InputComponent,
    TextareaComponent,
    SpinnerComponent,
    SearchComponent,
  }
})
export default class LandingTextsPage extends Vue {
  isPageLoading: boolean = true;
  isEditModalShown: boolean = false;
  rows: SearchTexts.LandingRow[] = [];
  selectedRow: SearchTexts.LandingRow = null;
  selectedForm: SearchTexts.DescriptionHtmlForm = new SearchTexts.DescriptionHtmlForm();

  query: string = '';
  statusFilter: string = 'all';
  groupFilter: string = 'all';

  @State tags: Project.TagsResponse;

  statusList: UI.DropdownItem[] = [
    { value: 'all', name: 'Все' },
    { value: SearchTexts.LandingStatus.TODO, name: 'Не заполнено' },
    { value: SearchTexts.LandingStatus.FILLED, name: 'Заполнено' },
    { value: SearchTexts.LandingStatus.MISSING, name: 'Нет текста' },
  ];

  groupList: UI.DropdownItem[] = [
    { value: 'all', name: 'Все группы' },
    { value: Project.TagGroupType.MAIN, name: 'Основное' },
    { value: Project.TagGroupType.MINI, name: 'Мини-игры' },
    { value: Project.TagGroupType.MOD, name: 'Моды' },
    { value: Project.TagGroupType.PLUGIN, name: 'Плагины' },
  ];

  groupLabels: Record<string, string> = {
    [Project.TagGroupType.MAIN]: 'Основное',
    [Project.TagGroupType.MINI]: 'Мини-игры',
    [Project.TagGroupType.MOD]: 'Моды',
    [Project.TagGroupType.PLUGIN]: 'Плагины',
  };

  get frontUrl(): string {
    return (process.env.VUE_APP_FRONT_URL || '').replace(/\/$/, '');
  }

  get allTags(): Project.Tag[] {
    if (!this.tags) return [];
    return Object.values(this.tags).reduce(
      (acc: Project.Tag[], g: Project.TagGroup) => (g && g.items ? [...acc, ...g.items] : acc),
      [] as Project.Tag[]
    );
  }

  get filteredRows(): SearchTexts.LandingRow[] {
    const q = this.query.trim().toLowerCase();
    return this.rows.filter(r => {
      if (this.statusFilter !== 'all' && r.status !== this.statusFilter) return false;
      if (this.groupFilter !== 'all' && r.group !== this.groupFilter) return false;
      if (q && !r.name.toLowerCase().includes(q) && !r.slug.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  get todoCount(): number {
    return this.rows.filter(r => r.status === SearchTexts.LandingStatus.TODO).length;
  }

  get missingCount(): number {
    return this.rows.filter(r => r.status === SearchTexts.LandingStatus.MISSING).length;
  }

  expectedType(group: string): SearchTexts.VersionTypeHtml {
    return group === Project.TagGroupType.MOD
      ? SearchTexts.VersionTypeHtml.PCMODS
      : SearchTexts.VersionTypeHtml.ALLSERVERS;
  }

  urlFor(group: string, slug: string): string {
    return group === Project.TagGroupType.MOD ? `/pc-mods/${slug}` : `/${slug}`;
  }

  statusFor(content: SearchTexts.DescriptionHtml | null): SearchTexts.LandingStatus {
    if (!content) return SearchTexts.LandingStatus.MISSING;
    const t = (content.t_text || '').trim();
    return (!t || t === 'TODO') ? SearchTexts.LandingStatus.TODO : SearchTexts.LandingStatus.FILLED;
  }

  statusLabel(s: string): string {
    return SearchTexts.LandingStatusLabels[s] || s;
  }

  statusColor(s: string): string {
    if (s === SearchTexts.LandingStatus.FILLED) return 'green--text';
    if (s === SearchTexts.LandingStatus.TODO) return 'red--text';
    return 'transparent-grey--text';
  }

  groupLabel(g: string): string {
    return this.groupLabels[g] || g;
  }

  buildRows(vds: SearchTexts.VersionDescriptionItem[], descs: SearchTexts.DescriptionHtml[]) {
    const descById = new Map<number, SearchTexts.DescriptionHtml>(descs.map(d => [d.id, d]));
    const order: Record<string, number> = {
      [SearchTexts.LandingStatus.TODO]: 0,
      [SearchTexts.LandingStatus.MISSING]: 1,
      [SearchTexts.LandingStatus.FILLED]: 2,
    };

    this.rows = this.allTags.map(tag => {
      const type = this.expectedType(tag.group);
      const vd = vds.find(v => v.version_display === tag.value && v.types === type);
      const content = vd ? (descById.get(vd.description) || null) : null;
      const status = this.statusFor(content);
      return {
        tagId: tag.id,
        name: tag.name,
        slug: tag.value,
        group: tag.group,
        type,
        url: this.urlFor(tag.group, tag.value),
        status,
        descriptionId: content ? content.id : null,
        content,
      } as SearchTexts.LandingRow;
    }).sort((a, b) => (order[a.status] - order[b.status]) || a.name.localeCompare(b.name));
  }

  openEditModal(row: SearchTexts.LandingRow) {
    if (row.status === SearchTexts.LandingStatus.MISSING || !row.content) return;
    this.selectedRow = row;
    this.selectedForm = new SearchTexts.DescriptionHtmlForm(row.content);
    this.$nextTick(() => {
      this.isEditModalShown = true;
    });
  }

  validateForm(): boolean {
    const f = this.selectedForm;
    let ok = true;
    const required: Array<{ field: UI.FormField, max: number }> = [
      { field: f.title, max: 150 },
      { field: f.description, max: 200 },
      { field: f.t_header, max: 150 },
      { field: f.t_text, max: 550 },
    ];
    for (const { field, max } of required) {
      if (!field.value || !field.value.trim()) { field.error = 'Поле пустое'; ok = false; }
      else if (field.value.length > max) { field.error = `Максимум ${max} символов`; ok = false; }
      else field.error = '';
    }
    if (f.b_header.value && f.b_header.value.length > 150) { f.b_header.error = 'Максимум 150 символов'; ok = false; }
    else f.b_header.error = '';
    return ok;
  }

  async saveText() {
    this.selectedForm.isLoading = true;
    this.selectedForm.error = '';
    try {
      if (!this.validateForm()) return;

      const payload = {
        title: this.selectedForm.title.value,
        description: this.selectedForm.description.value,
        t_header: this.selectedForm.t_header.value,
        t_text: this.selectedForm.t_text.value,
        b_header: this.selectedForm.b_header.value,
        b_text: this.selectedForm.b_text.value,
      };
      const resp = await updateDescription(this.selectedForm.id, payload);

      if (this.selectedRow) {
        this.selectedRow.content = resp.data;
        this.selectedRow.status = this.statusFor(resp.data);
      }
      this.closeModals();
    } catch (error) {
      const errResponse = error && (error as any).data ? (error as any).data : {};
      Object.keys(errResponse).forEach(key => {
        if (this.selectedForm[key] && this.selectedForm[key] instanceof UI.FormField) {
          this.selectedForm[key].error = errorHandler(key, errResponse[key][0]);
        } else {
          this.selectedForm.error = errorHandler(key, errResponse[key][0]);
        }
      });
    } finally {
      this.selectedForm.isLoading = false;
    }
  }

  closeModals() {
    this.isEditModalShown = false;
    this.$nextTick(() => {
      this.selectedForm = new SearchTexts.DescriptionHtmlForm();
      this.selectedRow = null;
    });
  }

  async mounted() {
    this.isPageLoading = true;
    try {
      const [vdResp, descResp] = await Promise.all([getVersionDescriptions(), getDescriptions()]);
      this.buildRows(vdResp.data, descResp.data);
    } catch (error) {
      console.log(error);
    } finally {
      this.isPageLoading = false;
    }
  }
}
